import { Fragment, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { aud } from '@/utils/format';
import { payoutHistory, type PayoutRun } from '@/domain/payoutFlow';
import { arrangementLabel } from '@/screens/payouts/payoutLabels';
import ReportDateFilter from '@/components/reporting/ReportDateFilter';
import type { StudioId } from '@/domain/types';
import clsx from 'clsx';
import { dayKey } from '@/utils/studioDay';

interface Props {
  studioId: StudioId;
  studioName: string;
  /** The sim clock — the "today" marker on the range picker. */
  dayIso: string;
}

const stateMeta: Record<PayoutRun['status'], { label: string; pill: string }> = {
  signed: { label: 'Signed', pill: 'pill-mint' },
  deferred: { label: 'Deferred', pill: 'pill-blood' },
  sent: { label: 'Awaiting artist', pill: 'pill-cream' },
  due: { label: 'Payable', pill: 'pill-gold' },
};

const dayLabel = (day: string): string => format(new Date(`${day}T12:00:00+10:00`), 'EEE d MMM');

/**
 * The business-side payout register (History tab). One row per WORK day, grouped
 * into the payment that settled it, newest payment first.
 *
 * The two date columns are the point of the screen. `Paid` is the day the cash
 * moved; `Payment for` is the day the work happened. They differ whenever a day
 * was short and had to wait — an artist is paid whole or not at all, so a payment
 * can cover several days, and a single-date register could not show that at all.
 *
 * Reads `payoutHistory`; never recomputes an amount.
 */
export default function PayoutHistoryPanel({ studioId, studioName, dayIso }: Props) {
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const artists = useBrainStore((s) => s.artists);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const today = dayKey(dayIso);
  // Opens on the fortnight ending today: enough to hold a carried day and its
  // settlement without burying them in a month of rows.
  const [from, setFrom] = useState(() => dayKey(new Date(new Date(`${today}T12:00:00+10:00`).getTime() - 13 * 86_400_000).toISOString()));
  const [to, setTo] = useState(today);
  const [artistId, setArtistId] = useState<string>('all');

  const studioArtists = useMemo(
    () =>
      Object.values(artists)
        .filter((a) => a.studioId === studioId)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artists, studioId],
  );

  const runs = useMemo(
    () =>
      payoutHistory({
        studioId,
        fromDayIso: from,
        toDayIso: to,
        ...(artistId === 'all' ? {} : { artistId }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, from, to, artistId, payoutRecords],
  );

  const paidAud = runs
    .filter((r) => r.status === 'signed')
    .reduce((sum, r) => sum + r.totalAud, 0);
  const owedAud = runs
    .filter((r) => r.status === 'deferred')
    .reduce((sum, r) => sum + r.totalAud, 0);
  const artistCount = new Set(runs.map((r) => r.artistId)).size;

  return (
    <div className="panel p-4" data-testid="payout-history-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-small-caps text-gold-bright">Payout history · {studioName}</p>
          <p className="font-serif text-cream-soft text-xs mt-1">
            Every envelope handed over, and every one still waiting. <span className="text-cream">Paid</span> is
            the day the cash moved; <span className="text-cream">payment for</span> is the day it was earned.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={artistId}
            onChange={(e) => setArtistId(e.target.value)}
            aria-label="Artist"
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="payout-history-artist"
          >
            <option value="all">All artists</option>
            {studioArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))}
          </select>
          <ReportDateFilter
            from={from}
            to={to}
            todayIso={clockIso}
            onChange={(nextFrom, nextTo) => {
              setFrom(nextFrom);
              setTo(nextTo);
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px]">
        <span className="text-cream-faint">
          {artistCount} artist{artistCount === 1 ? '' : 's'}
        </span>
        <span className="text-mint">{aud(paidAud)} paid</span>
        {owedAud > 0 ? (
          <span className="text-blood" data-testid="payout-history-owed">
            {aud(owedAud)} still owed
          </span>
        ) : null}
      </div>

      {runs.length === 0 ? (
        <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-4">
          No payouts in this range.
        </p>
      ) : (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-inter text-[10px] uppercase tracking-widest text-cream-faint border-b border-gold-dim/20">
                <th className="py-2 pr-3 font-normal">Paid</th>
                <th className="py-2 pr-3 font-normal">Payment for</th>
                <th className="py-2 pr-3 font-normal">Artist</th>
                <th className="py-2 pr-3 font-normal">Split</th>
                <th className="py-2 pr-3 font-normal text-right">Amount</th>
                <th className="py-2 font-normal">State</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const meta = stateMeta[run.status];
                // A payment covering more than one work day gets a gold rail, so a
                // bundle reads as one payment rather than two coincidences.
                const bundled = run.records.length > 1;
                return (
                  <Fragment key={run.id}>
                    {run.records.map((r, idx) => (
                      <tr
                        key={r.dayIso}
                        className={clsx(
                          'border-b border-gold-dim/10',
                          bundled && 'bg-gold-bright/5',
                        )}
                        data-testid={`history-row-${run.artistId}-${r.dayIso}`}
                      >
                        <td
                          className={clsx(
                            'py-2 pr-3 font-mono',
                            bundled && 'border-l-2 border-gold-bright pl-2',
                            idx === 0 ? 'text-cream' : 'text-cream-faint text-xs italic',
                          )}
                        >
                          {idx > 0
                            ? 'same payment'
                            : run.paidOnDayIso
                              ? dayLabel(run.paidOnDayIso)
                              : '— not yet'}
                        </td>
                        <td className="py-2 pr-3 font-mono text-cream">{dayLabel(r.dayIso)}</td>
                        <td className="py-2 pr-3 text-cream-soft">{run.artistName}</td>
                        <td className="py-2 pr-3 text-cream-faint">{arrangementLabel(r.arrangement)}</td>
                        <td className="py-2 pr-3 text-right font-mono text-cream">{aud(r.amountDueAud)}</td>
                        <td className="py-2">
                          <span className={`text-[10px] ${meta.pill}`}>{meta.label}</span>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
